// src/components/NotificationBell.tsx
// The bell in both portal headers: a dot when something needs attention,
// and a list of what it is, each linking to where it's dealt with.
// Rechecked every minute and whenever the page changes.

import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useGetNotificationsQuery } from "../app/api/notificationsApi";
import type { NotificationItem } from "../app/api/notificationsApi";

const TONE_DOT: Record<NotificationItem["tone"], string> = {
  urgent: "bg-[#c0392b]",
  action: "bg-[#c9a227]",
  info: "bg-[#3d5a9e]",
};

export default function NotificationBell({ skip = false }: { skip?: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  const { data, refetch, isError } = useGetNotificationsQuery(undefined, {
    skip,
    pollingInterval: 60_000,
    refetchOnFocus: true,
  });
  const items = data?.data.items ?? [];

  // Close on outside click; recheck after moving to another page
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  useEffect(() => {
    setOpen(false);
    if (!skip) refetch();
  }, [pathname, skip, refetch]);

  if (skip) return null;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative rounded-xl border border-[#dde2ec] p-2 text-[#5a6278] transition hover:bg-[#f1f3f8]"
        aria-label={items.length ? `Notifications: ${items.length}` : "Notifications"}
        aria-expanded={open}
      >
        <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" aria-hidden>
          <path
            d="M8 1.5a5 5 0 015 5v1.5l1 1.5v1H2v-1l1-1.5V6.5a5 5 0 015-5zM6.5 13.5a1.5 1.5 0 003 0"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {items.length > 0 && (
          <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#c0392b] px-1 text-[10.5px] font-bold leading-none text-white">
            {items.length > 9 ? "9+" : items.length}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-[#dde2ec] bg-white shadow-[0_16px_48px_rgba(27,45,94,0.14)]">
          <p className="border-b border-[#ebeff7] bg-[#f9fafc] px-4 py-3 text-sm font-bold text-[#16213e]">Notifications</p>
          {isError ? (
            <p className="px-4 py-5 text-sm text-[#5f6780]">Couldn't load notifications. Try again in a moment.</p>
          ) : items.length === 0 ? (
            <p className="px-4 py-5 text-sm text-[#5f6780]">You're all caught up.</p>
          ) : (
            <ul className="max-h-[60vh] divide-y divide-[#ebeff7] overflow-y-auto">
              {items.map((n) => (
                <li key={n.id}>
                  <Link to={n.to} className="flex gap-3 px-4 py-3 transition hover:bg-[#f1f3f8]">
                    <span className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${TONE_DOT[n.tone]}`} aria-hidden />
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-[#16213e]">{n.title}</span>
                      <span className="block text-xs leading-5 text-[#5f6780]">{n.body}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
